import { useState } from 'react';
import { Trash2 } from 'lucide-react';

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@design-system';

import { useWizardDistributionPartnerBlacklist } from './use-wizard-distribution-partner-blacklist';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function PartnerBlacklistAdmin() {
  const { entries, isLoading, addEntry, isAdding, removeEntry, isRemoving } =
    useWizardDistributionPartnerBlacklist();

  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const emailValid = EMAIL_PATTERN.test(email.trim());

  const add = () => {
    if (!emailValid) return;

    addEntry(
      { email: email.trim().toLowerCase(), note: note.trim() || null },
      {
        onSuccess: () => {
          setEmail('');
          setNote('');
        },
      },
    );
  };

  return (
    <Card className="max-w-5xl border border-gray-200 shadow-sm">
      <CardHeader>
        <CardTitle>Distribution Partners Deny List</CardTitle>
        <CardDescription className="text-gray-500">
          Hide the Distribution Partners feature from specific people by their exact email — for
          users whose email isn't on a partner's domain. Wizard users are never affected.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          {isLoading && <p className="text-muted-foreground text-sm">Loading…</p>}
          {!isLoading && entries.length === 0 && (
            <p className="text-muted-foreground text-sm">No one on the deny list yet.</p>
          )}
          {entries.map(entry => (
            <div
              key={entry.id}
              className="flex items-center justify-between gap-3 rounded-md border p-3"
            >
              <div className="min-w-0">
                <p className="text-foreground truncate text-sm font-medium">{entry.email}</p>
                {entry.note && (
                  <p className="text-muted-foreground truncate text-xs">{entry.note}</p>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                disabled={isRemoving}
                aria-label={`Remove ${entry.email} from deny list`}
                onClick={() => removeEntry(entry.id)}
                className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0"
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))}
        </div>

        <div className="space-y-3 border-t pt-6">
          <p className="text-foreground text-sm font-medium">Add an email</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-12 sm:items-end">
            <div className="sm:col-span-5">
              <Label className="text-muted-foreground text-xs">Email</Label>
              <Input
                type="email"
                value={email}
                placeholder="person@example.com"
                onChange={event => setEmail(event.target.value)}
              />
            </div>
            <div className="sm:col-span-5">
              <Label className="text-muted-foreground text-xs">Note (optional)</Label>
              <Input
                value={note}
                placeholder="Why they're excluded"
                onChange={event => setNote(event.target.value)}
              />
            </div>
            <div className="sm:col-span-2">
              <Button
                type="button"
                onClick={add}
                disabled={isAdding || !emailValid}
              >
                Add
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
